import {useCallback, useEffect, useMemo} from 'react';

import {CarouselItemProps} from '@/app/shared-components/Carousel';
import {ECardCategoryThemesIds} from '@/dataSource/src/api/cards-theme/entities';
import {useCardThemeV2Repository} from '@/dataSource/src/api/cards-theme/useCardThemeRepository';
import useWindowSize from '@/hooks/WindowSize/WindowSize';

export const ITEMS_PER_SLIDE_DESKTOP = 3;
export const ITEMS_PER_SLIDE_MOBILE = 2;

export interface CardThemesCarouselModel {
  category: ECardCategoryThemesIds;
  themes: CarouselItemProps[];
}

export const useActions = () => {
  //#region [---- [REPOSITORY] ----]
  const {
    cardThemes,
    isGetCardThemesV2Loading,
    isGetCardThemesV2Error,
    handlerGetCardThemes,
  } = useCardThemeV2Repository();
  //#endregion

  //#region [---- [PROPERTIES] ----]
  const {isMobile} = useWindowSize();
  //#endregion

  //#region [---- [HOOKS] ----]
  const itemPerSlide = useMemo(
    () => (isMobile ? ITEMS_PER_SLIDE_MOBILE : ITEMS_PER_SLIDE_DESKTOP),
    [isMobile],
  );

  const cardCategoryThemes: CardThemesCarouselModel[] = useMemo(() => {
    if (!cardThemes) return [];
    return cardThemes?.map(item => ({
      category: item.category,
      themes: item.themes.map(themeName => ({
        id: themeName,
        image: {
          source: `/assets/images/cards/MoCardFront-${themeName}.webp`,
        },
      })),
    }));
  }, [cardThemes]);

  useEffect(() => {
    if (!cardThemes) handlerGetCardThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardThemes]);
  //#endregion

  //#region [---- [LOGIC] ----]
  const getCardCategoryLabel = useCallback(
    (cardCategory: ECardCategoryThemesIds): string => {
      switch (cardCategory) {
        case ECardCategoryThemesIds.DIY:
          return 'DIY';
        case ECardCategoryThemesIds.THEMES:
          return 'Themes';
        case ECardCategoryThemesIds.REGIONAL:
          return 'Regional';
        case ECardCategoryThemesIds.MO_CLASSICS:
        default:
          return 'MoClassics';
      }
    },
    [],
  );
  //#endregion

  return {
    cardCategoryThemes,
    isGetCardThemesV2Loading,
    isGetCardThemesV2Error,
    itemPerSlide,
    getCardCategoryLabel,
  };
};
